import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { meetingService, Meeting } from '../../services/meetingService';
import { formatDate } from '../../utils/dateUtils';

interface CMSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
  onSuccess?: () => void;
}

export const CMSummaryModal: React.FC<CMSummaryModalProps> = ({
  isOpen,
  onClose,
  meeting,
  onSuccess,
}) => {
  const [summaryText, setSummaryText] = useState('');
  const [originalSummaryText, setOriginalSummaryText] = useState('');
  const [isApproved, setIsApproved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    meetingService.getCMSummary(meeting.id)
      .then(({ content, summary_approved }) => {
        setSummaryText(content);
        setOriginalSummaryText(content);
        setIsApproved(!!summary_approved);
      })
      .catch(() => toast.error('Failed to load CM summary'))
      .finally(() => setIsLoading(false));
  }, [isOpen, meeting.id]);

  const hasChanged = summaryText !== originalSummaryText;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await meetingService.updateCMSummary(meeting.id, summaryText);
      setOriginalSummaryText(summaryText);
      // Saving clears approval — the summary must be re-approved after an edit.
      setIsApproved(false);
      toast.success('Summary saved successfully');
      onSuccess?.();
    } catch {
      toast.error('Failed to save summary');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    setIsSaving(true);
    try {
      await meetingService.updateCMSummary(meeting.id, summaryText, true);
      setOriginalSummaryText(summaryText);
      setIsApproved(true);
      toast.success('Summary approved successfully');
      onSuccess?.();
      handleClose(true);
    } catch {
      toast.error('Failed to approve summary');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = (force = false) => {
    if (!force && hasChanged) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    }
    setSummaryText('');
    setOriginalSummaryText('');
    onClose();
  };

  if (!isOpen) return null;

  const meetingDate = formatDate(meeting.scheduled_at);
  const meetingTypeName = meeting.meeting_type?.name ?? 'CM Meeting';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1400] p-4">
      <div
        className="bg-[#B4D9D4] rounded-3xl px-8 pb-8 w-full shadow-2xl relative max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative pt-3 pb-3">
          <div className="flex items-center justify-center">
            <h2 className="text-2xl font-bold text-[#366A75]">{meetingTypeName} Summary</h2>
            <button
              onClick={() => handleClose()}
              className="absolute right-0 text-[#366A75] hover:text-[#2D5A60] transition-colors disabled:opacity-50"
              disabled={isSaving}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-3">
          {/* Meeting info */}
          <div className="flex items-center gap-2 text-sm text-[#366A75] mb-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <span>{meetingDate}</span>
            <span className="text-[#366A75]/60">·</span>
            <span>{meeting.patient.full_name}</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#366A75]" />
            </div>
          ) : (
            <textarea
              value={summaryText}
              onChange={(e) => setSummaryText(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent h-[460px] resize-none"
              placeholder="No summary content available"
              disabled={isSaving}
              style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', lineHeight: '1.6', color: '#366A75' }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-4 pt-1 flex-shrink-0">
          <button
            onClick={handleApprove}
            disabled={isSaving || isLoading || isApproved}
            title={isApproved ? 'Summary already approved' : undefined}
            className="px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Approve
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading || !hasChanged}
            className="px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Save
          </button>
          <button
            onClick={() => handleClose()}
            disabled={isSaving}
            className="px-8 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
